import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { HttpBaseService } from 'src/app/base/http-base.service';
import UserStoryActions from '../../actions/generations/userstory.actions';
import { catchError, EMPTY, finalize, map, of, switchMap, tap } from 'rxjs';
import { Store } from '@ngrx/store';
import { AppLoaderService } from 'src/app/services/app-loader.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class GenerationUserStoriesEffects {
  private readonly actions = inject(Actions);
  private readonly httpService = inject(HttpBaseService);
  private readonly store = inject(Store);
  private readonly loaderService = inject(AppLoaderService);
  private readonly translateService = inject(TranslateService);

  getAllUserStories$ = createEffect(() =>
    this.actions.pipe(
      ofType(UserStoryActions.getAllUserStories),
      tap(() => {
        this.loaderService.show();
      }),
      switchMap((action: any) => {
        const { apiConstants, urlPathVariables } = action;
        return this.httpService[apiConstants.method](apiConstants, urlPathVariables).pipe(
          map((response: any) => UserStoryActions.setNormalizedData({ data: response })),
          finalize(() => {
            this.loaderService.hide();
          })
        );
      })
    )
  );
  createUserStory$ = createEffect(() =>
    this.actions.pipe(
      ofType(UserStoryActions.createUserStory),
      tap(() => {
        this.loaderService.show();
      }),
      switchMap((action: any) => {
        const { apiConstants, data } = action;
        return this.httpService[apiConstants.method](apiConstants, data).pipe(
          map((response: any) => {
            this.httpService.showMessage(
              'success',
              this.translateService.instant('COMPONENT_ADDED_SUCCESSFULLY', { component: 'UserStory' })
            );
            return UserStoryActions.createUserStorySuccess({ data: response });
          }),
          finalize(() => {
            this.loaderService.hide();
          }),
          catchError((error) => {
            return of(UserStoryActions.createUserStoryFailure({ error }));
          })
        );
      })
    )
  );
  createUserStorySuccess$ = createEffect(() =>
    this.actions.pipe(
      ofType(UserStoryActions.createUserStorySuccess),
      map((response: any) => {
        return UserStoryActions.addUserStoryInState({ data: response.data });
      })
    )
  );

  createProcessFailure$ = createEffect(
    () =>
      this.actions.pipe(
        ofType(UserStoryActions.createUserStoryFailure),
        map((error: any) => {
          console.error('Error creating user story:', error);
        })
      ),
    { dispatch: false }
  );
  updateUserStory$ = createEffect(() =>
    this.actions.pipe(
      ofType(UserStoryActions.updateUserStory),
      tap(() => {
        this.loaderService.show();
        console.log('[EFFECT] updateUserStory effect triggered');
      }),
      switchMap((action: any) => {
        const { apiConstants, data } = action;
        return this.httpService[apiConstants.method](apiConstants, data).pipe(
          map((response: any) => {
            this.httpService.showMessage(
              'success',
              this.translateService.instant('COMPONENT_UPDATED_SUCCESSFULLY', { component: 'Userstory' })
            );
            return UserStoryActions.updateUserStoryInState({ data: response });
          }),
          finalize(() => {
            this.loaderService.hide();
          }),
          catchError((error) => {
            console.error('Error updating process:', error);
            return EMPTY;
          })
        );
      })
    )
  );
  deleteUserStory$ = createEffect(() =>
    this.actions.pipe(
      ofType(UserStoryActions.deleteUserStory),
      tap(() => {
        this.loaderService.show();
      }),
      switchMap((action: any) => {
        const { apiConstants, urlPathVariables } = action;
        return this.httpService[apiConstants.method](apiConstants, urlPathVariables).pipe(
          map((response: any) => {
            this.httpService.showMessage(
              'success',
              this.translateService.instant('COMPONENT_DELETED_SUCCESSFULLY', { component: 'User Story' })
            );
            return UserStoryActions.deleteUserStoryInState({ id: urlPathVariables.componentId });
          }),
          finalize(() => {
            this.loaderService.hide();
          })
        );
      })
    )
  );
}
